import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ChatBubbleBottomCenterTextIcon, PlusIcon, TrashIcon, BookmarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkIconSolid } from '@heroicons/react/24/solid';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { BrainIcon } from './icons/BrainIcon';
import { SearchIcon } from './icons/SearchIcon';

const ChatHistory = ({ onLoadThread, onNewChat, currentChatId, isVisible = true, refreshTrigger = 0 }) => {
  const [chatHistory, setChatHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const { user } = useAuth();

  // Memoize loadChatHistory to fix useEffect dependency warning
  const loadChatHistory = useCallback(async () => {
    if (!user) {
      setDebugInfo('No user authenticated');
      return;
    }
    
    setLoadingHistory(true);
    setDebugInfo(`Loading for user: ${user.id}`);
    
    try {
      const { data, error } = await supabase
        .from('chat_history')
        .select('*')
        .eq('user_id', user.id)
        .eq('hidden_by_user', false) // Only show non-hidden conversations
        .order('pinned', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (error) {
        console.error('ChatHistory: Error loading from database:', error);
        setDebugInfo(`Database error: ${error.message}`);
        throw error;
      }
      
      setDebugInfo(`Found ${data?.length || 0} raw entries`);
      
      // Process conversations (each entry is now a conversation thread)
      const conversations = data?.map(entry => {
        let messageCount = 1;
        let firstQuestion = entry.question;
        let isPlaceholder = false;
        let conversationMode = 'search'; // Default mode
        
        // Check if this is a placeholder "New conversation"
        if (entry.question === "New conversation" && !entry.answer) {
          isPlaceholder = true;
        }
        
        // Check if this is a conversation thread (answer is JSON array)
        if (entry.answer && entry.answer.startsWith('[')) {
          try {
            const conversationHistory = JSON.parse(entry.answer);
            messageCount = conversationHistory.length;
            firstQuestion = conversationHistory[0]?.question || entry.question;
            // Get the most recent mode from the conversation
            conversationMode = conversationHistory[conversationHistory.length - 1]?.mode || 
                             entry.search_metadata?.last_chat_mode || 
                             entry.search_metadata?.chat_mode || 
                             'search';
          } catch (parseError) {
            console.warn('Error parsing conversation history:', parseError);
          }
        } else {
          // Single Q&A format - check metadata for mode
          conversationMode = entry.search_metadata?.chat_mode || 
                           entry.search_metadata?.last_chat_mode || 
                           'search';
        }
        
        // Extract the base question without message count
        const baseQuestion = firstQuestion.replace(/ \(\d+ messages\)$/, '');
        
        // For placeholders, show different preview text
        const preview = isPlaceholder 
          ? "New conversation..." 
          : baseQuestion.slice(0, 60) + (baseQuestion.length > 60 ? '...' : '');
        
        return {
          id: entry.id,
          session_id: entry.session_id,
          question: baseQuestion,
          preview: preview,
          timestamp: entry.created_at,
          created_at: entry.created_at,
          messageCount: messageCount,
          fullConversation: entry.answer,
          isPlaceholder: isPlaceholder,
          pinned: entry.pinned || false,
          mode: conversationMode // Add mode to chat history item
        };
      }) || [];
      
      setDebugInfo(`Processed ${conversations.length} unique conversations`);
      setChatHistory(conversations);
    } catch (error) {
      console.error('ChatHistory: Error loading chat history:', error);
      setDebugInfo(`Error: ${error.message}`);
    } finally {
      setLoadingHistory(false);
    }
  }, [user]);

  // Load chat history when user is available and component is visible
  useEffect(() => {
    if (user && isVisible) {
      loadChatHistory();
    } else if (!user) {
      setChatHistory([]);
    }
  }, [user, isVisible, loadChatHistory, currentChatId, refreshTrigger]); // Added refreshTrigger to refresh when it changes

  const formatRelativeTime = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffInHours = Math.abs(now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
    if (diffInHours < 48) return 'Yesterday';
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return date.toLocaleDateString();
  };

  const groupChatsByDate = (chats) => {
    // Separate pinned and unpinned conversations
    const pinnedChats = chats.filter(chat => chat.pinned);
    const unpinnedChats = chats.filter(chat => !chat.pinned);
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const thisWeek = new Date(today);
    thisWeek.setDate(thisWeek.getDate() - 7);
    
    const groups = {
      pinned: pinnedChats,
      today: [],
      yesterday: [],
      thisWeek: [],
      older: []
    };
    
    unpinnedChats.forEach(chat => {
      const chatDate = new Date(chat.timestamp);
      const chatDay = new Date(chatDate.getFullYear(), chatDate.getMonth(), chatDate.getDate());
      
      if (chatDay.getTime() === today.getTime()) {
        groups.today.push(chat);
      } else if (chatDay.getTime() === yesterday.getTime()) {
        groups.yesterday.push(chat);
      } else if (chatDate >= thisWeek) {
        groups.thisWeek.push(chat);
      } else {
        groups.older.push(chat);
      }
    });
    
    return groups;
  };

  const deleteChatThread = async (conversationId) => {
    if (!user) return;
    
    try {
      // Soft delete: hide from user view but keep in database for analytics
      const { error } = await supabase
        .from('chat_history')
        .update({ hidden_by_user: true })
        .eq('user_id', user.id)
        .eq('id', conversationId);
      
      if (error) throw error;
      
      // Remove from local state
      setChatHistory(prev => prev.filter(chat => chat.id !== conversationId));
      
      // If this was the current chat, start a new one
      if (currentChatId === conversationId) {
        onNewChat();
      }
    } catch (error) {
      console.error('Error hiding chat thread:', error);
    }
  };

  const togglePinConversation = async (conversationId, currentPinnedStatus) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('chat_history')
        .update({ pinned: !currentPinnedStatus })
        .eq('user_id', user.id)
        .eq('id', conversationId);
      
      if (error) throw error;
      
      // Update local state immediately for responsive UI
      setChatHistory(prev => prev.map(chat => 
        chat.id === conversationId 
          ? { ...chat, pinned: !currentPinnedStatus }
          : chat
      ));
      
      // Reload chat history to get proper sorting after a short delay
      setTimeout(() => {
        loadChatHistory();
      }, 300);
      
    } catch (error) {
      console.error('Error toggling pin status:', error);
      // Revert local state on error
      setChatHistory(prev => prev.map(chat => 
        chat.id === conversationId 
          ? { ...chat, pinned: currentPinnedStatus }
          : chat
      ));
    }
  };

  const groupedChats = groupChatsByDate(chatHistory);

  if (!user || !isVisible) {
    // Show debug info even when user is not available
    if (!user && isVisible) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800/50 border border-gray-700/50 rounded-xl backdrop-blur-sm p-4"
        >
          <div className="text-center text-gray-500">
            <ChatBubbleBottomCenterTextIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Authentication required</p>
            <p className="text-xs text-gray-600">Please sign in to view chat history</p>
          </div>
        </motion.div>
      );
    }
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800/50 border border-gray-700/50 rounded-xl backdrop-blur-sm h-full flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700/50 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <ChatBubbleBottomCenterTextIcon className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold text-white">Thread History</h3>
        </div>
        <div className="flex items-center space-x-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={loadChatHistory}
            className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-gray-700/50"
            title="Refresh History"
            disabled={loadingHistory}
          >
            <ArrowPathIcon className={`w-4 h-4 ${loadingHistory ? 'animate-spin' : ''}`} />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onNewChat}
            className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-gray-700/50"
            title="New Chat"
          >
            <PlusIcon className="w-4 h-4" />
          </motion.button>
        </div>
      </div>
      
      {/* Chat List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {loadingHistory ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : chatHistory.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <ChatBubbleBottomCenterTextIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No chat history yet</p>
            <p className="text-xs text-gray-600">Start a conversation to see it here</p>
            {debugInfo && (
              <div className="mt-4 text-xs text-gray-600 bg-gray-800/30 rounded p-2">
                Debug: {debugInfo}
              </div>
            )}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={loadChatHistory}
              className="mt-3 text-xs text-gray-500 hover:text-gray-400 transition-colors flex items-center justify-center gap-1"
            >
              <ArrowPathIcon className="w-3 h-3" />
              Refresh History
            </motion.button>
          </div>
        ) : (
          <>
            {Object.entries(groupedChats).map(([period, chats]) => 
              chats.length > 0 && (
                <div key={period}>
                  <h4 className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-2 px-2 flex items-center gap-2">
                    {period === 'pinned' ? (
                      <>
                        <BookmarkIconSolid className="w-3 h-3 text-purple-500" />
                        Bookmarked
                      </>
                    ) : (
                      period === 'today' ? 'Today' : 
                      period === 'yesterday' ? 'Yesterday' : 
                      period === 'thisWeek' ? 'This Week' : 'Older'
                    )}
                  </h4>
                  <div className="space-y-1">
                    {chats.map((chat, index) => (
                      <motion.div
                        key={chat.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`group hover:bg-gray-700/30 rounded-lg transition-all duration-200 border-l-2 ${
                          currentChatId === chat.id 
                            ? 'border-l-primary bg-primary/10 shadow-lg' 
                            : chat.pinned 
                              ? 'border-l-purple-500 bg-purple-500/5'
                              : 'border-l-transparent hover:border-l-gray-600'
                        }`}
                      >
                        <div className="p-3 relative">
                          {/* Top right action buttons */}
                          <div className="absolute top-1.5 right-1.5 flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                togglePinConversation(chat.id, chat.pinned);
                              }}
                              className={`p-2 rounded-lg bg-gray-800/80 backdrop-blur-sm transition-all duration-200 shadow-lg hover:shadow-xl ${
                                chat.pinned 
                                  ? 'text-purple-400 hover:text-purple-300 bg-purple-500/10 border border-purple-500/20' 
                                  : 'text-gray-400 hover:text-purple-400 hover:bg-purple-500/10 border border-gray-600/30 hover:border-purple-500/30'
                              }`}
                              title={chat.pinned ? "Remove bookmark" : "Bookmark conversation"}
                            >
                              <motion.div
                                key={chat.pinned ? 'pinned' : 'unpinned'}
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.2 }}
                              >
                                {chat.pinned ? (
                                  <BookmarkIconSolid className="w-3.5 h-3.5" />
                                ) : (
                                  <BookmarkIcon className="w-3.5 h-3.5" />
                                )}
                              </motion.div>
                            </motion.button>
                            
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                deleteChatThread(chat.id);
                              }}
                              className="p-2 rounded-lg bg-gray-800/80 backdrop-blur-sm text-gray-400 hover:text-red-400 transition-all duration-200 border border-gray-600/30 hover:border-red-500/30 shadow-lg hover:shadow-xl"
                              title="Delete conversation"
                            >
                              <TrashIcon className="w-3.5 h-3.5" />
                            </motion.button>
                          </div>

                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => onLoadThread(chat.id)}
                            className="w-full text-left relative z-0"
                          >
                            <div className="pr-20 mb-3">
                              <p className="text-sm text-white group-hover:text-primary line-clamp-2 leading-relaxed">
                                {chat.isPlaceholder ? (
                                  <span className="italic text-gray-400 flex items-center gap-2">
                                    <svg className="w-4 h-4 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    {chat.preview}
                                  </span>
                                ) : (
                                  chat.preview
                                )}
                              </p>
                            </div>
                            
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-gray-500">
                                {formatRelativeTime(chat.timestamp)}
                              </p>
                              <div className="flex items-center gap-2">
                                {chat.messageCount > 1 && (
                                  <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                                    {chat.messageCount} messages
                                  </span>
                                )}
                                {/* Mode pill with icon */}
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${
                                  chat.mode === 'coach' 
                                    ? 'bg-green-500/10 text-green-400 border border-green-500/20' 
                                    : 'bg-primary/10 text-primary-light border border-primary/20'
                                }`}>
                                  {chat.mode === 'coach' ? (
                                    <>
                                      <BrainIcon className="w-3 h-3" />
                                      <span>Coach</span>
                                    </>
                                  ) : (
                                    <>
                                      <SearchIcon className="w-3 h-3" />
                                      <span>Search</span>
                                    </>
                                  )}
                                </span>
                              </div>
                            </div>
                          </motion.button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )
            )}
          </>
        )}
      </div>
      
      {/* Refresh Button */}
      {chatHistory.length > 0 && (
        <div className="p-4 border-t border-gray-700/50 flex-shrink-0">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={loadChatHistory}
            disabled={loadingHistory}
            className="w-full text-sm text-gray-400 hover:text-white transition-colors py-2 rounded-lg hover:bg-gray-700/50 disabled:opacity-50"
          >
            {loadingHistory ? 'Refreshing...' : 'Refresh History'}
          </motion.button>
        </div>
      )}
    </motion.div>
  );
};

export default ChatHistory; 